import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Check, Search } from 'lucide-react';
import { listSkills } from '@/services/skillsApi';
import type { ManualSkill } from '@Types/skills';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

interface SkillPickerProps {
  anchorRef: React.RefObject<HTMLElement>;
  activeIds: Set<string>;
  onToggle: (skill: ManualSkill) => void;
  onClose: () => void;
  onOpenLibrary: () => void;
  initialQuery?: string;
}

const PICKER_WIDTH = 320; // matches w-80
const VIEWPORT_MARGIN = 8;

export const SkillPicker = ({
  anchorRef,
  activeIds,
  onToggle,
  onClose,
  onOpenLibrary,
  initialQuery = '',
}: SkillPickerProps) => {
  const [skills, setSkills] = useState<ManualSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(initialQuery);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    listSkills()
      .then(res => {
        if (!cancelled) setSkills([...res.tenant, ...res.user]);
      })
      .catch(() => {
        if (!cancelled) setSkills([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const popoverHeight = containerRef.current?.offsetHeight ?? 320;
      const top = rect.top - popoverHeight - 8;
      let left = rect.left;
      const maxLeft = window.innerWidth - PICKER_WIDTH - VIEWPORT_MARGIN;
      if (left > maxLeft) left = maxLeft;
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
      setPosition({
        top: Math.max(VIEWPORT_MARGIN, top),
        left,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, loading, skills.length]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose, anchorRef]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter(
      s =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q),
    );
  }, [skills, query]);

  return createPortal(
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        width: PICKER_WIDTH,
        visibility: position ? 'visible' : 'hidden',
      }}
      className="bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 px-2 py-2 border-b border-slate-200/80">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#768D95]" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search your skills"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-dark"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto max-h-72">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-dark border-t-transparent" />
          </div>
        ) : skills.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
            <Sparkles className="h-6 w-6 text-slate-300 mb-1" />
            <p className="text-xs text-[#252F33] font-medium">No skills yet</p>
            <p className="text-xs text-[#768D95] mt-0.5">
              Create a skill, or wait for a tenant admin to publish one.
            </p>
            <button
              type="button"
              onClick={() => {
                trackAdpEvent(
                  getBernoulliAdp(),
                  ADP_EVENTS.SKILLS_PICKER_OPEN_LIBRARY,
                  { context: 'empty_state' },
                );
                onClose();
                onOpenLibrary();
              }}
              className="mt-2 text-xs text-brand-link hover:underline font-medium">
              Manage skills
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-[#768D95]">
            No matches
          </div>
        ) : (
          <ul className="py-1">
            {filtered.map(skill => {
              const active = activeIds.has(skill.skill_id);
              return (
                <li key={skill.skill_id}>
                  <button
                    type="button"
                    onClick={() => {
                      trackAdpWaypoint(
                        getBernoulliAdp(),
                        ADP_EVENTS.SKILLS_PICKER_TOGGLE_SKILL,
                        {
                          skill_id: skill.skill_id,
                          skill_name: skill.name,
                          action: active ? 'remove' : 'add',
                          scope: skill.scope,
                        },
                      );
                      onToggle(skill);
                    }}
                    className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-[#F3F5F9] ${
                      active ? 'bg-[#E0E0E0]' : ''
                    }`}>
                    <div className="flex-shrink-0 w-5 h-5 mt-0.5 rounded border border-slate-300 flex items-center justify-center bg-white">
                      {active && (
                        <Check className="h-3.5 w-3.5 text-brand-dark" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-semibold text-[#252F33] truncate">
                          {skill.name}
                        </p>
                        {skill.scope === 'tenant' && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Tenant
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#768D95] line-clamp-2">
                        {skill.description}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {skills.length > 0 && (
        <div className="flex-shrink-0 border-t border-slate-200/80 px-3 py-1.5 bg-[#FAFBFC]">
          <button
            type="button"
            onClick={() => {
              trackAdpEvent(
                getBernoulliAdp(),
                ADP_EVENTS.SKILLS_PICKER_OPEN_LIBRARY,
                { context: 'footer' },
              );
              onClose();
              onOpenLibrary();
            }}
            className="text-xs text-brand-link hover:underline font-medium">
            Manage skills
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
};
